import type { PropsWithChildren } from 'react';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { baseUrl } from '../../config';
import type { HubConnection } from '@microsoft/signalr';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { generateApiRoute } from '@klp/core/api/helpers';

import { createContext } from 'react';
import type { ISignalRContext } from '../../interfaces';

const defaultSignalRContext: ISignalRContext = {
    connection: null,
    connected: false,

    startConnection: async () => {},

    stopConnection: async () => {},
};

export const SignalRContext = createContext<ISignalRContext>(
    defaultSignalRContext,
);

export const SignalRProvider = ({
    children,
    apiName,
    endpoint,
}: PropsWithChildren<{ endpoint: string; apiName: string }>) => {
    const connectionRef = useRef<HubConnection | null>(null);
    const [connected, setConnected] = useState(false);
    const hubUrl = `${baseUrl}${generateApiRoute([apiName, endpoint])}`;

    const createConnection = () => {
        if (connectionRef.current) return;

        const conn = new HubConnectionBuilder()
            .withUrl(hubUrl, { withCredentials: false })
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            .build();

        connectionRef.current = conn;
    };

    const deleteConnection = async () => {
        if (!connectionRef.current) return;

        try {
            await connectionRef.current.stop();
            connectionRef.current = null;
            setConnected(false);
        } catch (error) {
            console.error('SignalR disconnection error:', error);
        }
    };
    useEffect(() => {
        createConnection();

        return () => {
            deleteConnection();
        };
    }, []);

    const startConnection = async () => {
        if (!connectionRef.current) return;

        try {
            await connectionRef.current.start();
            setConnected(true);
        } catch (error) {
            console.error('SignalR connection error:', error);
        }
    };

    const stopConnection = async () => {
        if (!connectionRef.current) return;
        try {
            await connectionRef.current.stop();
            setConnected(false);
        } catch (error) {
            console.error('SignalR disconnection error:', error);
        }
    };

    const value = useMemo(
        () => ({
            connection: connectionRef.current,
            connected,
            startConnection,
            stopConnection,
        }),
        [connected],
    );

    return (
        <SignalRContext.Provider value={value}>
            {children}
        </SignalRContext.Provider>
    );
};
